import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../superadmin/DistrictsList.css';

const CSVReports = () => {
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState('');
  const [formDefinition, setFormDefinition] = useState(null);
  const [selectedFields, setSelectedFields] = useState({});
  const [reportName, setReportName] = useState('');
  const [savedReports, setSavedReports] = useState([]);
  const [selectedSavedReport, setSelectedSavedReport] = useState('');
  const [loading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [viewMode, setViewMode] = useState('select'); // 'select', 'view', 'download'

  useEffect(() => {
    fetchForms();
    fetchSavedReports();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await axios.get('/api/form-instances/district');
      console.log('Available forms for CSV:', response.data.map(f => ({ id: f.id, name: f.instance_name || f.master_form_name })));
      setForms(response.data);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    }
  };

  const fetchSavedReports = async () => {
    try {
      const response = await axios.get('/api/reports/csv/saved');
      setSavedReports(response.data);
    } catch (error) {
      console.error('Failed to fetch saved reports:', error);
    }
  };

  const handleFormChange = async (formId) => {
    setSelectedForm(formId);
    setSelectedFields({});
    setFormDefinition(null);

    if (formId) {
      try {
        const response = await axios.get(`/api/form-instances/${formId}`);
        setFormDefinition(response.data);

        // Initialize selected fields - check all fillable fields by default
        const initialSelected = {};
        if (response.data.form_data && response.data.form_data.sections) {
          response.data.form_data.sections.forEach(section => {
            if (section.fields) {
              section.fields.forEach(field => {
                // Only include fillable fields (not notes, info, etc.)
                if (field.type !== 'note' && field.type !== 'info' && field.name) {
                  initialSelected[field.name] = true;
                }
              });
            }
          });
        }
        setSelectedFields(initialSelected);
      } catch (error) {
        console.error('Failed to fetch form definition:', error);
      }
    }
  };

  const handleFieldToggle = (fieldName) => {
    setSelectedFields(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const handleSelectAllFields = () => {
    if (!formDefinition?.form_data?.sections) return;

    const allFields = {};
    formDefinition.form_data.sections.forEach(section => {
      if (section.fields) {
        section.fields.forEach(field => {
          if (field.type !== 'note' && field.type !== 'info' && field.name) {
            allFields[field.name] = true;
          }
        });
      }
    });
    setSelectedFields(allFields);
  };

  const handleDeselectAllFields = () => {
    setSelectedFields({});
  };

  const handleSaveReport = async () => {
    if (!reportName.trim()) {
      alert('Please enter a report name');
      return;
    }

    try {
      await axios.post('/api/reports/csv/saved', {
        name: reportName,
        form_instance_id: selectedForm,
        selected_fields: selectedFields
      });
      setReportName('');
      fetchSavedReports();
      alert('Report saved successfully!');
    } catch (error) {
      console.error('Failed to save report:', error);
      alert('Failed to save report');
    }
  };

  const handleLoadSavedReport = async (reportId) => {
    try {
      const response = await axios.get(`/api/reports/csv/saved/${reportId}`);
      const report = response.data;
      setSelectedForm(report.form_instance_id);
      setSelectedFields(report.selected_fields);

      // Load form definition
      const formResponse = await axios.get(`/api/form-instances/${report.form_instance_id}`);
      setFormDefinition(formResponse.data);
    } catch (error) {
      console.error('Failed to load saved report:', error);
      alert('Failed to load saved report');
    }
  };

  const generateCSV = async () => {
    if (!selectedForm || Object.keys(selectedFields).length === 0) {
      alert('Please select a form and at least one field');
      return;
    }

    setLoading(true);
    try {
      const selectedFieldsArray = Object.keys(selectedFields).filter(key => selectedFields[key]);
      console.log('Generating CSV for form:', selectedForm, 'with fields:', selectedFieldsArray);

      const response = await axios.post('/api/reports/csv/generate', {
        form_instance_id: selectedForm,
        selected_fields: selectedFieldsArray
      });

      setCsvData(response.data);
      setViewMode('view');
    } catch (error) {
      console.error('Failed to generate CSV:', error);
      alert('Failed to generate CSV report');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!csvData) return;

    const blob = new Blob([csvData.csv_content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${csvData.filename}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const renderFieldSelector = () => {
    if (!formDefinition?.form_data?.sections) return null;

    return (
      <div style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4>Select Fields to Include:</h4>
          <div>
            <button onClick={handleSelectAllFields} className="btn-secondary" style={{ marginRight: '0.5rem' }}>
              Select All
            </button>
            <button onClick={handleDeselectAllFields} className="btn-secondary">
              Deselect All
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          {formDefinition.form_data.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} style={{ flex: '1', minWidth: '300px' }}>
              <h5>{section.section_name || `Section ${sectionIndex + 1}`}</h5>
              {section.fields && section.fields.map((field, fieldIndex) => {
                // Skip non-fillable fields
                if (field.type === 'note' || field.type === 'info' || !field.name) return null;

                return (
                  <div key={fieldIndex} style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedFields[field.name] || false}
                        onChange={() => handleFieldToggle(field.name)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      {field.label || field.name}
                    </label>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <h5>Save as Custom Report (Optional)</h5>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Enter report name"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              style={{ flex: '1', padding: '0.5rem' }}
            />
            <button onClick={handleSaveReport} className="btn-primary">
              Save Report
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCSVViewer = () => {
    if (!csvData) return null;

    // Simple CSV viewer - split by lines and render as table
    const lines = csvData.csv_content.split('\n').filter(line => line.trim());
    const headers = lines[0]?.split(',') || [];
    const rows = lines.slice(1);

    return (
      <div style={{ width: '1920px', maxWidth: '75vw', marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4>CSV Report Preview</h4>
          <button onClick={downloadCSV} className="btn-primary">
            Download CSV
          </button>
        </div>

        <div style={{ overflow: 'auto', maxHeight: '600px', border: '1px solid #ddd', borderRadius: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                {headers.map((header, index) => (
                  <th key={index} style={{ padding: '0.75rem', border: '1px solid #ddd', textAlign: 'left', fontWeight: 'bold' }}>
                    {header.replace(/"/g, '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 100).map((row, rowIndex) => {
                const cells = row.split(',');
                return (
                  <tr key={rowIndex}>
                    {cells.map((cell, cellIndex) => (
                      <td key={cellIndex} style={{ padding: '0.75rem', border: '1px solid #ddd' }}>
                        {cell.replace(/"/g, '')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > 100 && (
            <div style={{ padding: '1rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
              Showing first 100 rows. Download CSV for complete data.
            </div>
          )}
        </div>
      </div>
    );
  };

  if (viewMode === 'view') {
    return (
      <div>
        <button onClick={() => setViewMode('select')} className="btn-secondary" style={{ marginBottom: '1rem' }}>
          ← Back to Report Builder
        </button>
        {renderCSVViewer()}
      </div>
    );
  }

  return (
    <div>
      {/* Saved Reports Selector */}
      <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
        <h4>Load Saved Report:</h4>
        <select
          value={selectedSavedReport}
          onChange={(e) => {
            setSelectedSavedReport(e.target.value);
            if (e.target.value) {
              handleLoadSavedReport(e.target.value);
            }
          }}
          style={{ padding: '0.5rem', minWidth: '200px' }}
        >
          <option value="">Select a saved report...</option>
          {savedReports.map(report => (
            <option key={report.id} value={report.id}>
              {report.name}
            </option>
          ))}
        </select>
      </div>

      {/* Form Selector */}
      <div style={{ marginBottom: '2rem' }}>
        <h4>Select Form:</h4>
        <select
          value={selectedForm}
          onChange={(e) => handleFormChange(e.target.value)}
          style={{ padding: '0.5rem', minWidth: '300px' }}
        >
          <option value="">Choose a form...</option>
          {forms.map(form => (
            <option key={form.id} value={form.id}>
              {form.instance_name || form.master_form_name}
            </option>
          ))}
        </select>
      </div>

      {/* Field Selector */}
      {renderFieldSelector()}

      {/* Generate Button */}
      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <button
          onClick={generateCSV}
          className="btn-primary"
          disabled={!selectedForm || Object.keys(selectedFields).filter(key => selectedFields[key]).length === 0 || loading}
          style={{ padding: '1rem 2rem', fontSize: '1.1rem' }}
        >
          {loading ? 'Generating CSV...' : 'Generate CSV Report'}
        </button>
      </div>
    </div>
  );
};

export default CSVReports;
